import { chartData } from './../models/dashboard.model';
import { Injectable } from '@angular/core';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
} from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, Title, CategoryScale, Tooltip);

@Injectable({
  providedIn: 'root'
})
export class RevenueChartLineService {
  private chart: Chart | undefined;

  initChart(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Canvas context not available');
      return;
    }

    this.chart = new Chart(ctx, {
      type: 'line',
      data: this.getChartData('yearly'),
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            mode: 'index',
            intersect: false,
            displayColors: false,
            callbacks: {
              title: () => '',
              label: (context) => `$${context.raw}`
            }
          },
          legend: {
            display: false
          }
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: '#535862',
              font: {
                family: 'Inter, sans-serif',
                size: 12,
              },
            },
          },
          y: {
            grid: {
              color: '#F3F4F6',
            },
            border: {
              display: false
            },
            ticks: {
              display: false
            },
          }
        },
      }
    });
  }

  updateChartData(period: string, chartData: chartData) {
    if (!this.chart) {
      console.error('Chart not initialized');
      return;
    }

    const newData = this.getChartData(period, chartData);
    this.chart.data.labels = newData.labels;
    this.chart.data.datasets[0].data = newData.datasets[0].data;
    this.chart.update();
  }

  refreshChart(period: string, chartData: chartData) {
    if (this.chart) {
      this.chart.destroy();
    }
    this.updateChartData(period, chartData);
  }

  getChartLabels(period: string, chartData?: chartData): string[] {
    return this.getChartData(period, chartData).labels as string[];
  }
  getChartValues(period: string, chartData?: chartData): number[] {
    return this.getChartData(period, chartData).datasets[0].data as number[];
  }
  private getChartData(period: string, chartData?: chartData) {
    switch (period) {
      case 'yearly':
        return {
          labels: chartData?.['12m']?.labels ?? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          datasets: [{
            label: 'Revenue',
            data: chartData?.['12m']?.values ?? Array(12).fill(0),
            borderColor: '#8B5CF6',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            borderWidth: 2,
            tension: 0,
            pointRadius: 0,
            pointHoverRadius: 6,
            fill: true,
          }]
        };
      case 'quarterly':
        return {
          labels: chartData?.['4q']?.labels ?? ['Q1', 'Q2', 'Q3', 'Q4'],
          datasets: [{
            label: 'Revenue',
            data: chartData?.['4q']?.values ?? Array(4).fill(0),
            borderColor: '#8B5CF6',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            borderWidth: 2,
            tension: 0,
            pointRadius: 0,
            pointHoverRadius: 6,
            fill: true,
          }]
        };
      case 'monthly':
        return {
          labels: chartData?.['30d']?.labels ?? Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`),
          datasets: [{
            label: 'Revenue',
            data: chartData?.['30d']?.values ?? Array(30).fill(0),
            borderColor: '#8B5CF6',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            borderWidth: 2,
            tension: 0,
            pointRadius: 0,
            pointHoverRadius: 6,
            fill: true,
          }]
        };
      case 'weekly':
        return {
          labels: chartData?.['7d']?.labels ?? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
          datasets: [{
            label: 'Revenue',
            data: chartData?.['7d']?.values ?? Array(7).fill(0),
            borderColor: '#8B5CF6',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            borderWidth: 2,
            tension: 0,
            pointRadius: 0,
            pointHoverRadius: 6,
            fill: true,
          }]
        };
      case 'daily':
        return {
          labels: chartData?.['1d']?.labels ?? Array.from({ length: 24 }, (_, i) => `${i}:00`),
          datasets: [{
            label: 'Revenue',
            data: chartData?.['1d']?.values ?? Array(24).fill(0),
            borderColor: '#8B5CF6',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            borderWidth: 2,
            tension: 0,
            pointRadius: 0,
            pointHoverRadius: 6,
            fill: true,
          }]
        };
      default:
        return { labels: [], datasets: [] };
    }
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
